import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { apiClient } from '@/api/client'
import { useQueryClient } from '@tanstack/react-query'

export interface User {
  id: string
  email: string
  name: string
  role: string
  tenantId: string
  permissions?: Record<string, Record<string, boolean>>
  effective_permissions?: Record<string, Record<string, boolean>>
  team_ids?: string[]
  teams?: Array<{ id: string; name: string; role: string; permissions: Record<string, Record<string, boolean>> }>
  assigned_role_id?: string
  assigned_role_name?: string
  is_super_admin?: boolean
  [key: string]: any
}

interface AuthContextValue {
  user: User | null
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string, name: string, tenantSlug: string) => Promise<any>
  logout: () => void
  loading: boolean
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const queryClient = useQueryClient()

  useEffect(() => {
    // Check for existing session on mount
    checkAuth()
  }, [])

  // Listen for auth:logout events from API interceptors
  useEffect(() => {
    const handleLogout = () => {
      localStorage.removeItem('token')
      localStorage.removeItem('refreshToken')
      setUser(null)
      queryClient.clear()
    }
    window.addEventListener('auth:logout', handleLogout)
    return () => window.removeEventListener('auth:logout', handleLogout)
  }, [queryClient])

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('token')
      if (token) {
        const response = await apiClient.get('/auth/me')
        setUser(response.data.user)
      }
    } catch (error) {
      localStorage.removeItem('token')
    } finally {
      setLoading(false)
    }
  }

  const login = async (email: string, password: string) => {
    // Clear all cached data from the previous user session
    queryClient.clear()

    const response = await apiClient.post('/auth/login', { email, password })
    const { token, refreshToken } = response.data
    
    localStorage.setItem('token', token)
    localStorage.setItem('refreshToken', refreshToken)
    // Trigger proactive refresh scheduling
    window.dispatchEvent(new Event('auth:token-updated'))
    const meResponse = await apiClient.get('/auth/me')
    setUser(meResponse.data.user)
  }

  const register = async (email: string, password: string, name: string, tenantSlug: string) => {
    // Clear all cached data
    queryClient.clear()

    const response = await apiClient.post('/auth/register', {
      email,
      password,
      name,
      tenantSlug
    })

    // If registration requires email verification, return response without setting tokens
    if (response.data.requiresVerification) {
      return response.data
    }

    // Fallback: if tokens are returned (e.g., admin-created accounts)
    const { token, refreshToken } = response.data
    if (token) {
      localStorage.setItem('token', token)
      localStorage.setItem('refreshToken', refreshToken)

      // Fetch the full user profile
      const meResponse = await apiClient.get('/auth/me')
      setUser(meResponse.data.user)
    }

    return response.data
  }

  const logout = () => {
    localStorage.removeItem('token')
    localStorage.removeItem('refreshToken')
    setUser(null)
    // Clear all cached data so the next user starts fresh
    queryClient.clear()
  }

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}